import {
    IIntegrationResponse,
    ILocationResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { DocumentTitle } from 'components/DocumentTitle';
import { DetailsPage } from 'components/page/DetailsPage';
import { TablePageLayout } from 'components/page/styled';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { RouteButtonTabs } from 'components/tabs/RouteButtonTabs';
import { TabRouter } from 'components/tabs/TabRouter';
import {
    IntegrationUploadHistoryPagedDataSource,
    usePagedIntegrationUploadHistoryDataSource,
} from 'custom_hooks/data_hooks/data_source/integrationUploadHistoryDataSource';
import { useRouteTabs } from 'custom_hooks/data_hooks/data_source/useRouteTabs';
import { ContentTabConfig } from 'custom_hooks/data_hooks/data_source/useTabs';
import { useIntegration } from 'custom_hooks/data_hooks/integrations/useIntegration';
import { IntegrationDetailsContent } from 'pages/Settings/Integrations/IntegrationDetailsContent';
import { IntegrationMappingHistoryTab } from 'pages/Settings/Integrations/tabs/IntegrationMappingHistoryTab';
import { IntegrationSettingsTab } from 'pages/Settings/Integrations/tabs/IntegrationSettingsTab';
import { MappingButtons } from 'pages/Settings/Integrations/tabs/MappingButtons';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { excludeNullAndUndefined } from 'utils/array';
import { getTabId } from 'utils/RouteHelper';

export type IntegrationDetailsPageProps = Readonly<{
    location: ILocationResponse;
    integrationId: string;
    onChange: () => void;
}>;

const getTabs = (
    integration: IIntegrationResponse,
    locationId: string,
    dataSource: IntegrationUploadHistoryPagedDataSource
): ContentTabConfig[] =>
    excludeNullAndUndefined([
        {
            id: 'settings',
            label: 'Settings',
            content: (
                <IntegrationSettingsTab
                    locationId={locationId}
                    integration={integration}
                />
            ),
        },
        {
            id: 'mapping-history',
            label: 'Mapping History',
            content: <IntegrationMappingHistoryTab dataSource={dataSource} />,
        },
    ]);

export function IntegrationDetailsPage({
    location,
    integrationId,
    onChange,
}: IntegrationDetailsPageProps) {
    const { pathname } = useLocation();
    const [selectedRouteTab, setSelectedRouteTab] = useState(getTabId(pathname));

    const { data: integration, isLoading, refetch } = useIntegration(
        integrationId,
        location.Id
    );

    const mappingDataSource = usePagedIntegrationUploadHistoryDataSource({
        name: 'integration-mapping-history-table',
        defaultFilter: {
            locationId: location.Id,
            integrationId,
        },
    });

    const onUpdateDetails = () => {
        refetch();
        onChange();
    };

    const { routeTabs } = useRouteTabs(
        useMemo(
            () =>
                integration ? getTabs(integration, location.Id, mappingDataSource) : [],
            [integration, location.Id, mappingDataSource]
        )
    );

    useEffect(() => {
        setSelectedRouteTab(getTabId(pathname));
    }, [pathname]);

    return (
        <DetailsPage
            isLoading={isLoading}
            invalidURL={!integration && !isLoading}
            fullHeight
            fullWidth
        >
            <SpinnerArea isLoading={isLoading}>
                {integration && (
                    <>
                        <DocumentTitle
                            title={`${location.Name} ${integration.SoftwareName} Details`}
                        />
                        <IntegrationDetailsContent
                            location={location}
                            integration={integration}
                            onUpdate={onUpdateDetails}
                        />
                        <TablePageLayout className="my-20">
                            <div className="flex-row mb-20">
                                <RouteButtonTabs routeTabs={routeTabs} />
                                {selectedRouteTab === 'mapping-history' && (
                                    <MappingButtons
                                        location={location}
                                        integration={integration}
                                        dataSource={mappingDataSource}
                                    />
                                )}
                            </div>
                            <TabRouter routeTabs={routeTabs} />
                        </TablePageLayout>
                    </>
                )}
            </SpinnerArea>
        </DetailsPage>
    );
}
